import { z } from 'zod';
import type { AnyHint, FieldHint, ListOptions } from '../types';

/**
 * A list field: many rows of the same few boxes, as one form key holding an array.
 *
 * `listOf` takes the row's shape and the row's hints together and returns the pieces a
 * form spreads into its schema and its field hint, so the two cannot disagree about
 * what a row contains — the same guarantee the Entra catalogue picker gives at the
 * top level. The registry tests check the result anyway.
 */
export interface ListField<Shape extends z.ZodRawShape> {
  /** One row. */
  row: z.ZodObject<Shape>;
  /** The whole list, sized by `list`. Spread this as the form key's schema. */
  schema: z.ZodArray<z.ZodObject<Shape>>;
  /** Spread these two into the field hint alongside `widget: 'list'`. */
  columns: readonly FieldHint<z.input<z.ZodObject<Shape>>>[];
  list: ListOptions;
}

export function listOf<Shape extends z.ZodRawShape>(
  rowShape: Shape,
  columns: readonly FieldHint<z.input<z.ZodObject<Shape>>>[],
  options: ListOptions,
): ListField<Shape> {
  const row = z.object(rowShape);
  const plural = `${options.rowLabel}s`;
  const schema = z
    .array(row)
    .min(
      options.min,
      options.min === 1
        ? `Add at least one ${options.rowLabel}`
        : `Add at least ${options.min} ${plural}`,
    )
    .max(options.max, `No more than ${options.max} ${plural}`);
  return { row, schema, columns, list: options };
}

/** The row schema of a list field's array schema, through any optional wrapper. */
export function rowSchemaOf(schema: z.ZodType): z.ZodObject<z.ZodRawShape> | undefined {
  let current: z.ZodType = schema;
  while (current instanceof z.ZodOptional || current instanceof z.ZodDefault) {
    current = current.unwrap() as z.ZodType;
  }
  if (!(current instanceof z.ZodArray)) return undefined;
  const element = current.element as z.ZodType;
  return element instanceof z.ZodObject ? (element as z.ZodObject<z.ZodRawShape>) : undefined;
}

export type ListRow = Record<string, unknown>;

/** A cell as it is compared: trimmed, and for anything address-like, case-folded. */
function normalise(column: AnyHint, value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const text = String(value).trim();
  return column.widget === 'email' ? text.toLowerCase() : text;
}

/** True when a row has anything in it at all. */
export function isBlankRow(columns: readonly AnyHint[], row: ListRow): boolean {
  return columns.every((column) => {
    const value = row[column.name];
    return value === undefined || value === null || value === '' || value === false;
  });
}

/**
 * What identifies a row: the key columns joined, or `undefined` when the row has none
 * of them filled in — such a row can never match another, so it is always its own.
 * Without declared key columns a row's identity is everything in it, which makes only
 * an exact duplicate a match.
 */
export function rowKey(hint: AnyHint, row: ListRow): string | undefined {
  const columns = hint.columns ?? [];
  const keyNames = hint.list?.key ?? columns.map((column) => column.name);
  const parts = keyNames.map((name) => {
    const column = columns.find((c) => c.name === name);
    return column ? normalise(column, row[name]) : '';
  });
  return parts.some((part) => part !== '') ? parts.join('\u0000') : undefined;
}

function sameRow(columns: readonly AnyHint[], a: ListRow, b: ListRow): boolean {
  return columns.every(
    (column) => normalise(column, a[column.name]) === normalise(column, b[column.name]),
  );
}

export interface MergeResult {
  rows: ListRow[];
  added: number;
  updated: number;
  unchanged: number;
}

/**
 * Bring uploaded rows into the rows already in the form, idempotently. Uploading the
 * same file twice changes nothing; uploading a corrected file updates the rows it
 * corrects in place; rows the form did not have are appended. Blank rows in the form
 * are dropped on the way, since an upload is what fills them.
 */
export function mergeRows(
  hint: AnyHint,
  existing: readonly ListRow[],
  incoming: readonly ListRow[],
): MergeResult {
  const columns = hint.columns ?? [];
  const rows = existing.filter((row) => !isBlankRow(columns, row));
  const byKey = new Map<string, number>();
  rows.forEach((row, index) => {
    const key = rowKey(hint, row);
    if (key !== undefined && !byKey.has(key)) byKey.set(key, index);
  });

  let added = 0;
  let updated = 0;
  let unchanged = 0;
  for (const row of incoming) {
    const key = rowKey(hint, row);
    const index = key === undefined ? undefined : byKey.get(key);
    if (index === undefined) {
      rows.push(row);
      if (key !== undefined) byKey.set(key, rows.length - 1);
      added += 1;
    } else if (sameRow(columns, rows[index]!, row)) {
      unchanged += 1;
    } else {
      rows[index] = row;
      updated += 1;
    }
  }
  return { rows, added, updated, unchanged };
}
